/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Tue Mar 26 2024
 **/

import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";

import { Row, Typography } from "@/frameworks/designStuff/components";
import { SwitchCase } from "@/frameworks/libraries/components";
import { _T } from "@rfice/language";
import { JotaiStore } from "@/frameworks/libraries/commons";

import { PhoneCallAtoms, usePhoneCall } from "@/frameworks/core/hooks";

type DialingStateProps = Partial<{
	typoSize?: DesignStuff.Typography.TypoSizeType | undefined;
	typoColor?: keyof Colors | undefined;
	call: PushServices.Call.Info;
	callID: string;
	isCallEnded: boolean;
}>;

export default function DialingState({
	call,
	callID,
	typoSize = "Title1_B",
	isCallEnded,
	typoColor
}: DialingStateProps) {
	const totalCallTime = useAtomValue(PhoneCallAtoms.CallTime(callID ?? ""), { store: JotaiStore });
	const callStatus = useAtomValue(PhoneCallAtoms.CallStatus(callID ?? ""));
	const phoneCall = usePhoneCall();
	const { myCallJoinState } = phoneCall.state.active;
	function formatTime(totalSeconds: number) {
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
	}

	return (
		<SwitchCase
			condition={myCallJoinState?.status === "ENDED" ? "END" : callStatus ?? "IDLE"}
			cases={{
				READY: (
					<Typography typoSize={typoSize} typoColor={typoColor ?? "neutral_80_100"}>
						{_T("전화 거는 중...")}
					</Typography>
				),
				ONGOING: (
					<Row gap={10} style={{ minWidth: "90px" }}>
						<Typography typoSize={typoSize} typoColor={typoColor ?? "success_60_100"}>
							{_T("통화중")}
						</Typography>
						<Typography typoSize={typoSize} typoColor={typoColor ?? "success_60_100"}>
							{formatTime(totalCallTime || 0)}
						</Typography>
					</Row>
				),
				END: (
					<Typography typoSize={typoSize} typoColor={typoColor ?? "neutral_80_100"}>
						{_T("종료됨")}
						{totalCallTime > 0 && ` ${formatTime(totalCallTime || 0)}`}
					</Typography>
				)
			}}
			defaultCase={
				<>
					{isCallEnded && (
						<Typography typoSize={typoSize} typoColor={typoColor ?? "neutral_80_100"}>
							{_T("종료됨")}
							{totalCallTime > 0 && ` ${formatTime(totalCallTime)}`}
						</Typography>
					)}
				</>
			}
		/>
	);
}
